/**
 * Shared helpers for create-menu and edit-menu components.
 * Encapsulates the optionType numeric↔string duality and validation logic.
 */

// Bidirectional mapping: string key ↔ numeric value
export const OPTION_TYPE_MAP: Record<string, number> = {
  categories:       1,
  subCategories:    2,
  subSubcategories: 3,
  collections:      4,
  custom:           5,
  components:       6,
  pages:            7,
};

const REVERSE_MAP: Record<number, string> = Object.entries(OPTION_TYPE_MAP)
  .reduce((acc, [k, v]) => { acc[v] = k; return acc; }, {} as Record<number, string>);

// Translation keys for type badges
export const OPTION_TYPE_LABELS: Record<string, string> = {
  categories:       'CATEGORY',
  subCategories:    'SUBCATEGORY',
  subSubcategories: 'SUB_SUBCATEGORIES',
  collections:      'COLLECTIONS',
  custom:           'EXTERNAL_LINK',
  components:       'COMPONENT_LINK',
  pages:            'PAGES',
};

/** Convert any optionType (string or number) to its numeric form (1-7). Returns 0 if unknown. */
export function toNumericOptionType(type: string | number): number {
  if (typeof type === 'number') return type;
  return OPTION_TYPE_MAP[type] ?? 0;
}

/** Convert any optionType (string or number) to its string form. Returns '' if unknown. */
export function toStringOptionType(type: string | number): string {
  if (typeof type === 'string' && OPTION_TYPE_MAP[type] !== undefined) return type;
  if (typeof type === 'number') return REVERSE_MAP[type] ?? '';
  return '';
}

/** Universal comparator: normalizes both sides to string before comparing. */
export function isOptionType(type: string | number, target: string): boolean {
  return toStringOptionType(type) === target;
}

/** Returns the i18n translation key for the option type badge. */
export function getOptionTypeLabel(type: string | number): string {
  const key = toStringOptionType(type);
  return OPTION_TYPE_LABELS[key] ?? '';
}

// Which field must be filled per type
const REQUIRED_FIELD_MAP: Record<string, string> = {
  categories:       'categoryId',
  subCategories:    'subCategoryId',
  subSubcategories: 'subSubCategoryId',
  collections:      'collectionId',
  custom:           'optionUrl',
  components:       'componentId',
  pages:            'pageId',
};

export function getRequiredFieldForType(type: string | number): string {
  return REQUIRED_FIELD_MAP[toStringOptionType(type)] ?? '';
}

const URL_PATTERN = /^https?:\/\/[\w\-]+(\.[\w\-]+)+([\/\w\-._~:?#[\]@!$&'()*+,;=]*)?$/;

export function isValidHttpUrl(url: string): boolean {
  if (!url) return false;
  return URL_PATTERN.test(url);
}

export interface OptionValidationResult {
  valid: boolean;
  errorType: 'required' | 'url' | null;
}

/** Validate a single option (root or child) regardless of type format (numeric or string). */
export function isOptionValid(opt: any): OptionValidationResult {
  if (!opt) return { valid: false, errorType: 'required' };

  if (!opt.optionName?.trim() || !opt.optionType) {
    return { valid: false, errorType: 'required' };
  }

  const strType = toStringOptionType(opt.optionType);
  if (!strType) return { valid: false, errorType: 'required' };

  if (strType === 'custom') {
    if (!opt.optionUrl || !isValidHttpUrl(opt.optionUrl)) {
      return { valid: false, errorType: 'url' };
    }
    return { valid: true, errorType: null };
  }

  const field = REQUIRED_FIELD_MAP[strType];
  if (field && !opt[field]) {
    return { valid: false, errorType: 'required' };
  }

  return { valid: true, errorType: null };
}

/** Factory: create a blank menu option with all fields zeroed. */
export function createBlankOption(position: number): any {
  return {
    optionType:       0,
    optionName:       '',
    optionNameEn:     '',
    manualTranslation: false,
    optionId:         0,
    categoryId:       0,
    subCategoryId:    0,
    subSubCategoryId: 0,
    collectionId:     0,
    optionUrl:        '',
    position,
    componentId:      0,
    pageId:           0,
    children:         [],
  };
}

/** Factory: create a blank sub-option. */
export function createBlankSubOption(position: number): any {
  return {
    optionType:       0,
    optionName:       '',
    optionNameEn:     '',
    manualTranslation: false,
    optionId:         0,
    categoryId:       0,
    subCategoryId:    0,
    subSubCategoryId: 0,
    collectionId:     0,
    optionUrl:        '',
    componentId:      0,
    pageId:           0,
    children:         [],
    position,
  };
}
